import { useQuery, useMutation, useQueryClient } from 'react-query'
import { alertsService } from '@/services/alertsService'
import { useAuth } from '@/contexts/AuthContext'
import type { Alert, AlertsBootstrap } from '@/types/alerts'

export const ALERTS_KEY = ['alerts', 'bootstrap'] as const
const MAX_ITEMS = 20

/** Reducer puro: aplica um alerta recebido por socket ao estado, com idempotência,
 *  limite de lista e contador não-negativo. */
export function applyNewAlert(
  state: AlertsBootstrap,
  alert: Alert
): AlertsBootstrap {
  if (state.items.some((a) => a.id === alert.id)) return state
  const items = [alert, ...state.items].slice(0, MAX_ITEMS)
  return { items, unreadCount: Math.max(0, state.unreadCount + 1) }
}

/** Reducer puro: alerta marcado como lido sai da lista (o sino só mostra não
 *  lidos). Só decrementa o contador quando ele estava realmente não lido. Se o
 *  servidor devolve `read_at` nulo (aviso em criação não se lê), nada muda. */
export function applyMarkedRead(
  state: AlertsBootstrap,
  updated: Alert
): AlertsBootstrap {
  if (updated.read_at == null) return state
  const wasUnread = state.items.some(
    (a) => a.id === updated.id && a.read_at == null
  )
  const items = state.items.filter((a) => a.id !== updated.id)
  return {
    items,
    unreadCount: wasUnread
      ? Math.max(0, state.unreadCount - 1)
      : state.unreadCount,
  }
}

/** Reducer puro do "marcar todas": limpa a lista, menos os avisos em criação
 *  (o servidor também não os conta como lidos). */
export function applyMarkedAllRead(state: AlertsBootstrap): AlertsBootstrap {
  const items = state.items.filter((a) => a.status === 'em_criacao')
  return { items, unreadCount: items.length }
}

export function useAlerts() {
  const qc = useQueryClient()
  const { token } = useAuth()
  const query = useQuery<AlertsBootstrap>(
    ALERTS_KEY,
    () => alertsService.getBootstrap(MAX_ITEMS),
    {
      staleTime: 30_000,
      enabled: !!token,
    }
  )

  const markRead = useMutation((id: number) => alertsService.markRead(id), {
    onSuccess: (updated) => {
      qc.setQueryData<AlertsBootstrap>(ALERTS_KEY, (prev) =>
        applyMarkedRead(prev ?? { items: [], unreadCount: 0 }, updated)
      )
    },
  })

  const markAllRead = useMutation(() => alertsService.markAllRead(), {
    onSuccess: () => {
      qc.setQueryData<AlertsBootstrap>(ALERTS_KEY, (prev) =>
        applyMarkedAllRead(prev ?? { items: [], unreadCount: 0 })
      )
    },
  })

  return {
    items: query.data?.items ?? [],
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
  }
}
